// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

export function isSet<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export function isNum(value: number | undefined | null): value is number {
  return isSet(value) && !isNaN(value);
}

// round the given number to max. number of decimals
export function round(num: number | undefined, digits?: number, scaleFactor?: number) {
  if (isSet(num) && !isNaN(num)) {
    const shownDigits = isSet(digits) ? digits : 2;
    const digitsExp = 10 ** shownDigits;
    const usedScaleFactor = isSet(scaleFactor) ? scaleFactor : 1;
    // determine whether we need to round
    return Math.round(num * usedScaleFactor * digitsExp) / digitsExp;
  }
  return NaN; // must not happen
}

// round the given number to exactly number of decimals
export function roundFix(num: number | undefined, digits?: number, scaleFactor?: number) {
  if (isSet(num) && !isNaN(num)) {
    const shownDigits = isSet(digits) ? digits : 2;
    const usedScaleFactor = isSet(scaleFactor) ? scaleFactor : 1;
    // determine whether we need to round
    return (num * usedScaleFactor).toFixed(shownDigits);
  }
  return "";
}

// round to x significant decimals; keep number if larger than significant digits
export function roundToDigits(num: number | undefined | null | string, digits?: number) {
  const parsed = typeof num === "string" ? parseFloat(num) : num;
  if (!isSet(parsed) || isNaN(parsed as any)) {
    return num;
  }
  // check for float underflow
  if (Math.abs(parsed) < 1e-10) {
    return 0;
  }
  const shownDigits = isSet(digits) ? digits : 2;
  const digitsExp = 10 ** shownDigits;
  // check if number is bigger than the digits to show; then we keep the whole number
  if (parsed >= digitsExp) {
    return Math.round(parsed);
  }
  return parseFloat(parsed.toPrecision(shownDigits));
}

// round to x significant digits but show all digits above comma
export function roundToMinDigits(num: number | undefined | null, digits?: number) {
  if (!isSet(num)) {
    return num;
  }
  // check for float underflow
  if (Math.abs(num) < 1e-10) {
    return 0;
  }

  const decades = Math.floor(Math.log10(num));
  if (!isSet(digits) || decades >= digits) {
    return Math.round(num);
  }
  return parseFloat(num.toPrecision(digits));
}

// format given number with thousand separators
export function formatNum(num: number | string) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export function getTimezoneDiff(deviceTimezoneOffset: number | undefined): number {
  // returns the timezone difference from device to browser in s
  if (!isSet(deviceTimezoneOffset)) {
    return 0;
  }
  // positive difference if local timezone is later than device timezone
  return -getBrowserTimezoneOffset() - deviceTimezoneOffset;
}

export function getBrowserTimezoneOffset(): number {
  // returns the browser timezone offset in s
  return 60 * new Date().getTimezoneOffset();
}

// timestamp in ms or as Date
export function getUtcDate(timestamp: Date | number | undefined) {
  if (isSet(timestamp)) {
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return date.toISOString().slice(0, 10);
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date
export function getLocalDate(timestamp: Date | number | undefined) {
  if (isSet(timestamp)) {
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      const dateStr = `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(
        -2,
      )}-${`0${date.getDate()}`.slice(-2)}`;
      return dateStr;
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date
export function getUtcTime(timestamp: Date | number | undefined): string {
  if (isSet(timestamp)) {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    try {
      return date.toISOString().slice(11, 19);
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date
export function getLocalTime(timestampMs: Date | number | undefined): string {
  if (isSet(timestampMs)) {
    try {
      // we need to construct the new Date() at the current day, otherwise the
      // hours might be wrong due to winterzeit/sommerzeit
      const dateOffset = Date.now() - (Date.now() % (24 * 3600 * 1000));
      const date =
        timestampMs instanceof Date
          ? timestampMs
          : new Date(timestampMs + (timestampMs < 24 * 3600 * 1000 ? dateOffset : 0));
      const dateStr = `${`0${date.getHours()}`.slice(-2)}:${`0${date.getMinutes()}`.slice(
        -2,
      )}:${`0${date.getSeconds()}`.slice(-2)}`;
      return dateStr;
    } catch {
      return "";
    }
  }
  return "";
}

// timestamp in ms or as Date; if undefined, get current time
export const getLocalDateTimeIso = (timestampMs: Date | number | undefined): string => {
  if (isSet(timestampMs)) {
    try {
      const date = timestampMs instanceof Date ? timestampMs : new Date(timestampMs);
      const newEpoch = date.getTime() - 1000 * getBrowserTimezoneOffset();
      const newTime = new Date(newEpoch);
      return newTime.toISOString().slice(0, 19);
    } catch {
      return "";
    }
  } else {
    return "";
  }
};

// timestamp in ms or as Date; if undefined, return empty string
export const getLocalDateTime = (timestampMs: Date | number | undefined): string => {
  return `${getLocalDate(timestampMs)} ${getLocalTime(timestampMs)}`;
};

export function formatTimestamp(timestamp: number) {
  if (isNaN(timestamp)) {
    return "";
  }
  if (timestamp < 3600) {
    return `${`0${Math.floor(timestamp / 60)}`.slice(-2)}:${`0${timestamp % 60}`.slice(-2)}`;
  }
  if (timestamp < 24 * 3600) {
    return `${`0${Math.floor(timestamp / 3600)}`.slice(-2)}:${`0${Math.floor(
      (timestamp % 3600) / 60,
    )}`.slice(-2)}:${`0${timestamp % 60}`.slice(-2)}`;
  }
  return `${Math.floor(timestamp / (24 * 3600))}T ${`0${Math.floor(
    (timestamp % (24 * 3600)) / 3600,
  )}`.slice(-2)}:${`0${Math.floor((timestamp % 3600) / 60)}`.slice(-2)}:${`0${
    timestamp % 60
  }`.slice(-2)}`;
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms, null));
}

export function rotateArray<DataType>(
  data: DataType[] | undefined,
  shift: number | undefined,
): DataType[] | undefined {
  if (!isSet(data)) {
    return undefined;
  }
  if (!isSet(shift)) {
    return data;
  }
  const newData = [...data]; // clone array
  //newData.unshift(...newData.splice(shift, newData.length));  // rotate left
  newData.unshift(...newData.splice(-shift)); // rotate right
  return newData;
}

export function formatBytes(bytes: number | undefined, decimals = 2) {
  if (!isSet(bytes)) {
    return "";
  }
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function JSONreplacer(key: string, value: any) {
  if (value instanceof Map) {
    return {
      dataType: "Map",
      value: Array.from(value.entries()), // or with spread: value: [...value]
    };
  } else {
    return value;
  }
}

export function JSONreviver(key: any, value: any): any {
  if (typeof value === "object" && value !== null) {
    if (value.dataType === "Map") {
      return new Map(value.value);
    }
  }
  return value;
}
